import React, { useState, useEffect, useContext } from 'react';
import 'ol/ol.css';
import '../../map/Map.css';
import OlMap from 'ol/Map';
import OlView from 'ol/View';
import { defaults as defaultControls } from 'ol/control';
import { scaleLine, attribution, mousePosition } from '../../map/MapControls';
import { OSM } from '../../map/MapLayers';
import { stylePolygonBasic } from '../../map/MapStyles';
import { Vector as OlVectorSource } from 'ol/source';
import { Vector as OlVectorLayer } from 'ol/layer';
import { GeoJSON } from 'ol/format';
import { Grid, Segment, Breadcrumb, Label, Button, List, Table, Icon } from 'semantic-ui-react';
import { Link, useHistory } from 'react-router-dom';
import _ from 'lodash';
import { UserContext } from '../../../app/layout/App';
import apiRequest from '../../../app/common/api/apiClient';

// ─── Label maps ───────────────────────────────────────────────────────────────

const ACTORS_LABELS = {
  individu_individu:     'Entre Individu ho Individu',
  individu_komunidade:   'Entre Individu ho Komunidade',
  individu_estado:       'Entre Indivídu ho Estado',
  komunidade_estado:     'Entre Komunidade ho Estado',
  komunidade_kompania:   'Entre Komunidade ho Empreza Privada',
  asosiasaun_governu:    'Entre Asosiasaun ka Fundasaun ho Estado',
  komunidade_igreija:    'Entre Komunidade ho Organizasaun Relijioza',
  komunidade_autoridade: 'Entre Komunidade ho Autoridade Lokal ka Lider Komunitariu',
  nst:                   'Nst',
  // anciennes valeurs DB (rétrocompatibilité affichage)
  komunidade_governu:    'Entre Komunidade ho Estado',
  komunidade_komunidade: 'Entre Komunidade ho Komunidade',
};

const SECONDARY_LABELS = {
  politiku:          'Politiku',
  polisia:           'Polisia',
  militar:           'Militar',
  autoridade_lokal:  'Autoridade Lokal no Lider Komunitariu',
  autoridade_igreija:'Autoridade Igreija (Padre/Madre, Leigo, Katekista nst)',
  veteranu:          'Veteranu',
  broker:            'Broker',
  nst:               'Nst',
};

const AREATYPE_LABELS = {
  mistura:        'Mistura (rezidensial no agrikultura uma-hun)',
  rezidensial:    'Rezidensial',
  agrikultura:    'Area agrikultura (toos, natar, viveiru, hakiak animal nst)',
  komunitaria:    'Propriedade komunitaria no zona protezida (ai-laran, fatin komunidade)',
  komersiu:       'Espasu komersiu (kiost, loja, fatin negosiu, fo arenda nst)',
  publiku_privadu:'Edifisiu/eskritoriu Publiku no Privadu',
  abandonadu:     'Rai abandonadu',
  nst:            'Nst',
};

const LANDTYPE_LABELS = {
  sinal_konstrusaun:  'Sinal Konstrusaun ruma (uma fatin antigu, monumentu nst)',
  konstrusaun_atual:  'Konstrusaun atual (Uma, bee matan, kiost nst)',
  produtu_plantasaun: 'Produtu/plantasaun (ai-han, ai-fuan, ai-horis, animal, kolam nst)',
  semeteriu:          'Semeteriu, uma lisan, fatin halo ritual nst',
  bee_matan:          'Bee matan no irigasaun',
  nst:                'Nst',
};

const LEGALSTATUS_LABELS = {
  propriedade_perfeita: 'Propriedade Perfeita (titulu tempu portuges)',
  hakmilik:             'Hakmilik (titulu tempu indonezia)',
  direitu_informal:     'Direitu Informal da Propriedade (propriedade komunitariu)',
  aforamentu:           'Aforamentu',
  hak_guna_bangunan:    'Hak Guna Bangunan',
  hak_guna_usaha:       'Hak Guna Usaha',
  kontratu_fan_sosa:    'Kontratu Fan no Sosa',
  susesaun:             'Susesaun tanba Mate/Eransa',
  uzukapiaun:           'Uzukapiaun (uza rai minimu tinan 20)',
  doasaun:              'Doasaun',
};

const KSISUPPORT_LABELS = {
  mediasaun:        'Mediasaun',
  refere_prosesu:   'Referé ba Prosesu Judisiáriu',
  sensibilizasaun:  'Sensibilizasaun Rejime Rai nian',
  konsolida_uniaun: 'Konsolida, mobiliza ba harii Uniaun',
  nst:              'Nst',
};

const PARTE_INTERESADA_LABELS = {
  individu:             'Individu',
  instituisaun_governu: 'Instituisaun Governu',
  igreija:              'Igreija',
  autoridade_lokal:     'Autoridade Lokal ka Lider Komunitariu',
  kompania_privada:     'Kompania Privada',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const LabelList = ({ value, map }) => {
  if (!value) return null;
  const items = value.split(',').map(v => v.trim()).filter(Boolean);
  return (
    <List bulleted>
      {items.map((v, i) => <List.Item key={i}>{map[v] || v}</List.Item>)}
    </List>
  );
};

const DetailLabel = ({ children }) => (
  <div style={{
    marginTop: '0.8rem',
    marginBottom: '0.2rem',
    borderLeft: '3px solid #00b5ad',
    paddingLeft: '0.5rem',
    fontSize: '0.85em',
    fontWeight: 'bold',
    color: '#555',
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
  }}>
    {children}
  </div>
);

// ─── OL setup (module-level to avoid re-creation) ────────────────────────────

const sourceOneProject = new OlVectorSource();
const vectorOneProject = new OlVectorLayer({
  title: 'Project',
  source: sourceOneProject,
  style: stylePolygonBasic,
});

// ─── Component ────────────────────────────────────────────────────────────────

const ProjectDetailPage = ({ match }) => {
  const idProject = match.params.id;
  const history = useHistory();
  const { state } = useContext(UserContext);
  const role = state.user?.type_utilisateur;

  const [project, setProject] = useState({});
  const [validated, setValidated] = useState(false);

  useEffect(() => {
    sourceOneProject.clear();

    const olmap = new OlMap({
      target: 'map',
      controls: defaultControls({ attribution: false }).extend([scaleLine, attribution, mousePosition]),
      layers: [OSM, vectorOneProject],
      view: new OlView({ projection: 'EPSG:3857', center: [0, 0], zoom: 2, minResolution: 5 }),
    });
    OSM.setVisible(true);

    sourceOneProject.on('change', () => {
      if (sourceOneProject.getFeatures().length > 0) {
        olmap.getView().fit(sourceOneProject.getExtent(), {
          padding: [30, 30, 30, 30], duration: 800, minResolution: 5,
        });
      }
    });

    apiRequest(`/api/cases/${idProject}`)
      .then(res => res.json())
      .then(data => {
        setProject(data.properties || {});
        setValidated(data.properties?.validated || false);
        if (data.geometry) {
          const feature = new GeoJSON().readFeature(
            { type: 'Feature', geometry: data.geometry, properties: {} },
            { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' }
          );
          sourceOneProject.addFeature(feature);
        }
      })
      .catch(err => console.error(err));

    return () => { olmap.setTarget(null); };
  }, [idProject]);

  const handleValidate = () => {
    apiRequest(`/api/cases/${idProject}/validate`, { method: 'PATCH' })
      .then(res => res.json())
      .then(() => setValidated(true))
      .catch(err => console.error(err));
  };

  const handleDelete = () => {
    if (!window.confirm('Supprimer ce cas ?')) return;
    apiRequest(`/api/cases/${idProject}`, { method: 'DELETE' })
      .then(() => history.push('/cases'))
      .catch(err => console.error(err));
  };

  const pop = (field) => parseInt(project[field]) > 0;

  return (
    <Grid>

      {/* ── Breadcrumb & actions ── */}
      <Grid.Row>
        <Grid.Column width={16}>
          <Breadcrumb>
            <Breadcrumb.Section as={Link} to='/cases'>Cases</Breadcrumb.Section>
            <Breadcrumb.Divider />
            <Breadcrumb.Section active>{_.capitalize(project.name)}</Breadcrumb.Section>
            {role !== 'user' && (
              <>
                <Breadcrumb.Divider />
                <Breadcrumb.Section as={Link} to={`/case/update/${idProject}`}>Update</Breadcrumb.Section>
              </>
            )}
          </Breadcrumb>
          <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem' }}>
            {(role === 'superadmin' || role === 'ksi') && !validated && (
              <Button size='small' color='green' icon labelPosition='left' onClick={handleValidate}>
                <Icon name='check' />Validate this case
              </Button>
            )}
            {validated && <Label color='green'><Icon name='check' />Case validated</Label>}
            {role === 'superadmin' && (
              <Button size='small' color='red' icon labelPosition='left' onClick={handleDelete}>
                <Icon name='trash' />Delete
              </Button>
            )}
          </div>
        </Grid.Column>
      </Grid.Row>

      {/* ══════════ ROW 1 : Description | Location ══════════ */}
      <Grid.Row>

        {/* Cadre 1 — Case description */}
        <Grid.Column width={10}>
          <Segment.Group>
            <Segment attached='top'><h5>Case description</h5></Segment>
            <Segment attached='bottom'>

              <DetailLabel>A.2. Títulu Kazu</DetailLabel>
              <p style={{ fontSize: '1.1em', fontWeight: 'bold' }}>{project.name}</p>

              {project.ref_kazu && (
                <>
                  <DetailLabel>A.1. Ref. Kazu</DetailLabel>
                  <p>{project.ref_kazu}</p>
                </>
              )}

              <DetailLabel>A.3. Munisipiu</DetailLabel>
              <p>{project.municipality || '—'}</p>

              {project.secondaryactors && (
                <>
                  <DetailLabel>B.5. Ema terseiru ne'ebe ameasa komunidade</DetailLabel>
                  <LabelList value={project.secondaryactors} map={SECONDARY_LABELS} />
                </>
              )}

              {project.juridicsituationclaming && (
                <>
                  <DetailLabel>B.6. Kronolojia Disputa Rai</DetailLabel>
                  <p style={{ whiteSpace: 'pre-wrap' }}>{project.juridicsituationclaming}</p>
                </>
              )}

            </Segment>
          </Segment.Group>
        </Grid.Column>

        {/* Cadre 1 droite — Location */}
        <Grid.Column width={6}>
          <Segment.Group>
            <Segment attached='top'><h5>Location</h5></Segment>
            <Segment attached='bottom'>
              <div id='map' style={{ width: '100%', height: '260px', marginBottom: '1rem' }} />
              {project.postu && (
                <>
                  <DetailLabel>Postu administrativu</DetailLabel>
                  <p>{project.postu}</p>
                </>
              )}
              {project.suku && (
                <>
                  <DetailLabel>Suku</DetailLabel>
                  <p>{project.suku}</p>
                </>
              )}
              {project.aldeia && (
                <>
                  <DetailLabel>Aldeia</DetailLabel>
                  <p>{project.aldeia}</p>
                </>
              )}
            </Segment>
          </Segment.Group>
        </Grid.Column>

      </Grid.Row>

      {/* ══════════ ROW 2 : Land use | Land & population ══════════ */}
      <Grid.Row>

        {/* Cadre 2 — Additional information on land use */}
        <Grid.Column width={10}>
          <Segment.Group>
            <Segment attached='top'><h5>Additional information on land use</h5></Segment>
            <Segment attached='bottom'>

              {project.areatype && (
                <>
                  <DetailLabel>A.4. Utilizasaun Bein Imovel Einkauza?</DetailLabel>
                  <LabelList value={project.areatype} map={AREATYPE_LABELS} />
                </>
              )}

              {project.landtype && (
                <>
                  <DetailLabel>A.5. Atividade iha Rai leten?</DetailLabel>
                  <LabelList value={project.landtype} map={LANDTYPE_LABELS} />
                </>
              )}

            </Segment>
          </Segment.Group>
        </Grid.Column>

        {/* Cadre 2 droite — Land & population */}
        <Grid.Column width={6}>
          <Segment.Group>
            <Segment attached='top'><h5>Information on land and population</h5></Segment>
            <Segment attached='bottom'>

              {project.conflictarea > 0 && (
                <>
                  <DetailLabel>B.2. Surface</DetailLabel>
                  <p>{parseFloat(project.conflictarea).toLocaleString('fr')} ha</p>
                </>
              )}

              {(pop('impactedhouseholds') || pop('impactedfamilymembers') || pop('impactedwomen') ||
                pop('impactedyouth') || pop('impacteddisabled') || pop('impactedelderly')) && (
                <>
                  <DetailLabel>B.4. Population afetadu</DetailLabel>
                  <Table celled compact size='small'>
                    <Table.Body>
                      {pop('impactedhouseholds') && (
                        <Table.Row>
                          <Table.Cell>Uma Kain</Table.Cell>
                          <Table.Cell textAlign='right'>{parseInt(project.impactedhouseholds).toLocaleString('fr')}</Table.Cell>
                        </Table.Row>
                      )}
                      {pop('impactedfamilymembers') && (
                        <Table.Row>
                          <Table.Cell>Membru Familia</Table.Cell>
                          <Table.Cell textAlign='right'>{parseInt(project.impactedfamilymembers).toLocaleString('fr')}</Table.Cell>
                        </Table.Row>
                      )}
                      {pop('impactedwomen') && (
                        <Table.Row>
                          <Table.Cell>Feto</Table.Cell>
                          <Table.Cell textAlign='right'>{parseInt(project.impactedwomen).toLocaleString('fr')}</Table.Cell>
                        </Table.Row>
                      )}
                      {pop('impactedyouth') && (
                        <Table.Row>
                          <Table.Cell>Joven (17–35)</Table.Cell>
                          <Table.Cell textAlign='right'>{parseInt(project.impactedyouth).toLocaleString('fr')}</Table.Cell>
                        </Table.Row>
                      )}
                      {pop('impacteddisabled') && (
                        <Table.Row>
                          <Table.Cell>Difisiente</Table.Cell>
                          <Table.Cell textAlign='right'>{parseInt(project.impacteddisabled).toLocaleString('fr')}</Table.Cell>
                        </Table.Row>
                      )}
                      {pop('impactedelderly') && (
                        <Table.Row>
                          <Table.Cell>Idozu</Table.Cell>
                          <Table.Cell textAlign='right'>{parseInt(project.impactedelderly).toLocaleString('fr')}</Table.Cell>
                        </Table.Row>
                      )}
                    </Table.Body>
                  </Table>
                </>
              )}

            </Segment>
          </Segment.Group>
        </Grid.Column>

      </Grid.Row>

      {/* ══════════ ROW 3 : Land conflict detail (colonnes égales) ══════════ */}
      <Grid.Row>

        {/* Cadre 3 gauche */}
        <Grid.Column width={8}>
          <Segment.Group>
            <Segment attached='top'><h5>Land conflict detail</h5></Segment>
            <Segment attached='bottom'>

              {project.actors && (
                <>
                  <DetailLabel>B.1. Parte sira husi Disputa</DetailLabel>
                  <LabelList value={project.actors} map={ACTORS_LABELS} />
                </>
              )}

              {project.legalstatusfarmerspov && (
                <>
                  <DetailLabel>B.4a. Fundamentu kona-ba Estatutu Rai (bele hili liu ida)</DetailLabel>
                  <LabelList value={project.legalstatusfarmerspov} map={LEGALSTATUS_LABELS} />
                </>
              )}

              {project.conflicthistory && (
                <>
                  <DetailLabel>Istória Komunidade</DetailLabel>
                  <p style={{ whiteSpace: 'pre-wrap' }}>{project.conflicthistory}</p>
                </>
              )}

            </Segment>
          </Segment.Group>
        </Grid.Column>

        {/* Cadre 3 droite */}
        <Grid.Column width={8}>
          <Segment.Group>
            <Segment attached='top'><h5>Parte Interesada / Benefisiária</h5></Segment>
            <Segment attached='bottom'>

              {project.parte_interesada_tipo && (
                <>
                  <DetailLabel>B.4b. Identifikasaun Parte Benefisiária</DetailLabel>
                  <LabelList value={project.parte_interesada_tipo} map={PARTE_INTERESADA_LABELS} />
                  {project.parte_interesada_individu && (
                    <p style={{ whiteSpace: 'pre-wrap', marginTop: '0.4rem', fontStyle: 'italic' }}>
                      {project.parte_interesada_individu}
                    </p>
                  )}
                </>
              )}

              {project.legalstatusotherpov && (
                <>
                  <DetailLabel>B.4c. Baze ba Parte Benefisiária</DetailLabel>
                  <LabelList value={project.legalstatusotherpov} map={LEGALSTATUS_LABELS} />
                </>
              )}

              {project.ownershipproffotherpov && (
                <>
                  <DetailLabel>Istória Parte Benefisiária</DetailLabel>
                  <p style={{ whiteSpace: 'pre-wrap' }}>{project.ownershipproffotherpov}</p>
                </>
              )}

            </Segment>
          </Segment.Group>
        </Grid.Column>

      </Grid.Row>

      {/* ══════════ ROW 4 : Envolvimentu KSI ka Uniaun ══════════ */}
      {(project.ksisupportstart || project.ksisupporttype || project.additionalinformation) && (
        <Grid.Row>
          <Grid.Column width={16}>
            <Segment.Group>
              <Segment attached='top'><h5>Informasaun adisionál (eventu ruma)</h5></Segment>
              <Segment attached='bottom'>
                {project.ksisupportstart && (
                  <>
                    <DetailLabel>C.1. Horibainhira maka KSI/KNRA hahu nia intervensaun? (tinan)</DetailLabel>
                    <p>{project.ksisupportstart}</p>
                  </>
                )}
                {project.ksisupporttype && (
                  <>
                    <DetailLabel>C.2. Tipu apoiu KSI/Uniaun</DetailLabel>
                    <LabelList value={project.ksisupporttype} map={KSISUPPORT_LABELS} />
                  </>
                )}
                {project.additionalinformation && (
                  <>
                    <DetailLabel>C.3. Informasaun adisionál (eventu ruma)</DetailLabel>
                    <p style={{ whiteSpace: 'pre-wrap' }}>{project.additionalinformation}</p>
                  </>
                )}
              </Segment>
            </Segment.Group>
          </Grid.Column>
        </Grid.Row>
      )}

    </Grid>
  );
};

export default ProjectDetailPage;
